import { DatasetState } from "@/store/slice/datasetSlice";
import { Dataset, WeightValueEntry, WeightValues } from "@/types/dataset";

import { getAvailableDatasets } from "./dataset";
import { getValidNumericAttributes } from "./datasetAttributes";

export type WeightPrimitive =
  | WeightValueEntry
  | string
  | number
  | undefined
  | null;

export const normalizeWeightEntry = (
  entry: WeightValueEntry,
  defaultWeight = "0"
): WeightValueEntry => {
  if (!entry) return { weight: defaultWeight };

  if (typeof entry === "string") return { weight: entry };

  return {
    weight: entry.weight ?? defaultWeight,
    ...(entry.negative_impact !== undefined
      ? { negative_impact: entry.negative_impact }
      : {})
  };
};

export const getWeightString = (entry?: WeightPrimitive): string => {
  if (entry === null || entry === undefined) return "";
  if (typeof entry === "number") {
    return Number.isFinite(entry) ? entry.toString() : "";
  }
  if (typeof entry === "string") {
    return entry;
  }

  return entry.weight ?? "";
};

export const getNegativeImpactValue = (
  entry?: WeightPrimitive
): boolean | undefined => {
  if (!entry || typeof entry === "string" || typeof entry === "number") {
    return undefined;
  }
  return entry.negative_impact;
};

export const getPercentageValue = (
  entry?: WeightPrimitive
): string | undefined => {
  if (!entry || typeof entry === "string" || typeof entry === "number") {
    return undefined;
  }
  return entry.percentage;
};

export const parseWeightValue = (entry?: WeightPrimitive): number => {
  const value = getWeightString(entry);
  if (!value) return 0;
  const parsed = Number.parseFloat(value.replace(/,/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const hasNonZeroValue = (entry?: WeightPrimitive | null): boolean => {
  return parseWeightValue(entry) !== 0;
};

export const calculateWeightPercentage = (
  datasetId: string,
  attributeIdx: string,
  totalWeight: number,
  localWeightValues: WeightValues
): string => {
  if (totalWeight === 0) return "0%";

  const datasetWeights = localWeightValues[datasetId];
  if (!datasetWeights) return "0%";

  const targetWeightEntry =
    attributeIdx === "weight"
      ? datasetWeights.weight
      : datasetWeights.attributes?.[attributeIdx];

  const weight = parseWeightValue(targetWeightEntry);

  const percentage = (weight / totalWeight) * 100;
  // Remove .00 if the percentage is a whole number
  const formattedPercentage = percentage.toFixed(2).replace(/\.00$/, "");
  return `${formattedPercentage}%`;
};

/** Helper function to recalculate all percentages
 * Extracted to allow calling from multiple reducers
 */
export const performRecalculateAllPercentages = (state: DatasetState) => {
  const existingDatasets = Object.values(getAvailableDatasets(state.datasets));

  if (!existingDatasets.length) return;

  let totalValidAttributes = 0;

  // Iterate through all datasets to find all valid attributes across datasets
  // Valid attributes are those that have INT or FLOAT data types, or are "Total POI Count" for POI datasets
  existingDatasets.forEach((dataset) => {
    const datasetId = dataset.data.id;

    // Initialize weight values object for the dataset if it doesn't exist
    if (!state.weightValues[datasetId]) {
      state.weightValues[datasetId] = { weight: "", attributes: {} };
    }

    // For dataset of "Polygon", "Admin Area", "H3", "Geohash" spatial type, filter attributes that have INT or FLOAT data types
    if (
      ["polygon", "adm_area", "H3", "geohash"].includes(
        dataset.properties.spatialType
      )
    ) {
      const validAttributes = getValidNumericAttributes(dataset.categories);

      // Count total valid attributes across datasets
      totalValidAttributes += validAttributes.length;

      // Initialize weight value for each valid attribute in the dataset
      validAttributes.forEach((attribute) => {
        if (!state.weightValues[datasetId].attributes[attribute.id]) {
          state.weightValues[datasetId].attributes[attribute.id] = {
            weight: "0"
          };
        }
      });
    }

    if (dataset.properties.spatialType === "poi") {
      // For dataset with "POI" spatial type, we only have 1 attribute called "Total POI Count"
      totalValidAttributes++;

      // "Total POI Count" is not a defined attribute/schema of a dataset in BE, but BE requires at least one attribute/schema to be defined and passed for the calculation
      // As per BE input, we can use any schemaId from the dataset
      const poiSchemaId = dataset.categories[0].id;

      // Initialize weight value for "Total POI Count" attribute in the dataset
      if (!state.weightValues[datasetId].attributes[poiSchemaId]) {
        state.weightValues[datasetId].attributes[poiSchemaId] = { weight: "0" };
      }
    }
  });

  // Calculate and assign the default weight value for each found attribute
  if (totalValidAttributes > 0) {
    // Equal distribution of weight values across all valid attributes
    // For example, if there are 4 valid attributes (of INT or FLOAT type), each will get a weight of 25 out of 100
    const defaultWeight = (100 / totalValidAttributes).toFixed(2).toString();

    // Assign the calculated default weight value
    Object.entries(state.weightValues).forEach(
      ([datasetId, weightAttributes]) => {
        Object.keys(weightAttributes.attributes).forEach((attributeId) => {
          state.weightValues[datasetId].attributes[attributeId] = {
            weight: defaultWeight
          };
        });
      }
    );
  }
};

/** Helper function to recalculate all percentages
 * Extracted to allow calling from multiple reducers
 */
export const performRecalculateAllPercentagesV2 = (state: DatasetState) => {
  const nonPOISpatialType = ["polygon", "adm_area", "H3", "geohash"];

  // Helper function to calculate default weight based on spatial type
  const getDefaultWeightForDataset = (dataset: Dataset): number => {
    if (nonPOISpatialType.includes(dataset.properties.spatialType)) {
      // For non-POI types: use count of valid numeric attributes
      const validAttributes = getValidNumericAttributes(dataset.categories);
      return validAttributes.length;
    } else if (dataset.properties.spatialType === "poi") return 1;

    // Default fallback: use all categories
    return dataset.categories.length;
  };

  // Step 1: Calculate total dataset weight
  let totalDatasetWeight = 0;

  Object.entries(getAvailableDatasets(state.datasets)).forEach(
    ([datasetId, dataset]) => {
      let weight = parseWeightValue(state.weightValues[datasetId]?.weight);

      // If dataset doesn't have weight initialized, use spatial type logic
      if (weight === 0 && !state.weightValues[datasetId]?.weight) {
        weight = getDefaultWeightForDataset(dataset);
      }

      totalDatasetWeight += weight;
    }
  );

  // Step 2: Recalculate attribute-level percentages
  Object.entries(state.datasets).forEach(([datasetId, dataset]) => {
    if (!state.weightValues[datasetId])
      state.weightValues[datasetId] = { weight: "", attributes: {} };

    // If dataset doesn't have weight initialized, use spatial type logic
    if (!state.weightValues[datasetId].weight) {
      state.weightValues[datasetId].weight =
        getDefaultWeightForDataset(dataset).toString();
    }

    // Calculate dataset-level percentage (used for calculating attribute percentages)
    const datasetPercentage = calculateWeightPercentage(
      datasetId,
      "weight",
      totalDatasetWeight,
      state.weightValues
    );

    const validAttributes = nonPOISpatialType.includes(
      dataset.properties.spatialType
    )
      ? getValidNumericAttributes(dataset.categories)
      : [dataset.categories[0]];

    // Ensure each attribute has at least a default weight before calculations
    validAttributes.forEach((attribute) => {
      const attributeId = attribute.id;
      if (!state.weightValues[datasetId].attributes[attributeId]) {
        state.weightValues[datasetId].attributes[attributeId] = {
          weight: "100"
        };
      }
    });

    // Calculate sum of attributes in this dataset
    let attributeWeightSum = 0;
    validAttributes.forEach((attribute) => {
      const weight = parseWeightValue(
        state.weightValues[datasetId]?.attributes[attribute.id]
      );
      attributeWeightSum += weight;
    });

    // Recalculate percentages for each attribute
    validAttributes.forEach((attribute) => {
      const attributeIdx = attribute.id;

      // Calculate attribute percentage within this dataset
      const attrPercentageWithinDataset = calculateWeightPercentage(
        datasetId,
        attributeIdx,
        attributeWeightSum,
        state.weightValues
      );

      // Calculate final attribute percentage: (attr % within dataset) × (dataset % of total)
      // Extract numeric value from datasetPercentage (e.g., "50%" -> 50)
      const datasetPercentageValue = parseFloat(datasetPercentage);
      // attr percentage should be: (attr % within dataset) * (dataset % of total) / 100
      const finalAttrPercentage =
        attributeWeightSum === 0
          ? "0"
          : (
              (parseFloat(attrPercentageWithinDataset) *
                datasetPercentageValue) /
              100
            ).toFixed(2);

      // Preserve existing weight value while updating percentage
      if (
        typeof state.weightValues[datasetId].attributes[attributeIdx] ===
          "object" &&
        state.weightValues[datasetId].attributes[attributeIdx] !== null
      ) {
        state.weightValues[datasetId].attributes[attributeIdx].percentage =
          finalAttrPercentage;
      } else {
        state.weightValues[datasetId].attributes[attributeIdx] = {
          weight: String(
            state.weightValues[datasetId].attributes[attributeIdx] ||
              parseWeightValue(
                state.weightValues[datasetId].attributes[attributeIdx]
              )
          ),
          percentage: finalAttrPercentage
        };
      }
    });
  });
};
